// The student-facing monitor for the Monitor screen. Opens in its own window (#live-monitor) for a second screen or an iPad's
// Split View, or sits beside the instructor's controls in the same window. Numbers ease toward each new set the way a
// real monitor trends; nothing shows for a sensor the crew hasn't attached, and the BP only changes when it's cycled.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { isRhythmOnlyPattern, normalizeRhythm, sampleLead } from "../ecg/ecgEngine";
import { openLiveChannel, readLiveState } from "./liveChannel";
import EcgPrintouts from "./EcgPrintouts";
import { cprArtifact } from "./liveModel";
import { BANDS, newbornSpo2Floor } from "../../utils/ageBands";
import "./live.css";

const COLORS = { ecg: "#3ee07a", pleth: "#43c6e8", co2: "#f5d547", nibp: "#e8ecef" };
const SHOCK_MS = 700; // how long a defibrillation spike and its recovery take on the trace
const SWEEP_MS = 6000; // screen width in time, like a 25 mm/s strip

// Pleth and capnography shapes, one cycle each, phase 0..1 -> 0..1
const plethAt = (p) => (p < 0.12 ? Math.pow(p / 0.12, 1.4) : Math.exp(-(p - 0.12) * 4.2) * 0.92 + 0.14 * Math.exp(-((p - 0.42) ** 2) / 0.003));
// Capnography: a square wave normally; a "shark fin" in bronchospasm, where the slow, obstructed exhale never plateaus.
const co2At = (p, shape) => {
  if (p < 0.06) return 0;
  if (shape === "shark") {
    if (p < 0.5) return (1 - Math.exp(-(p - 0.06) * 7)) / (1 - Math.exp(-0.44 * 7));
    if (p < 0.56) return Math.max(0, 1 - (p - 0.5) / 0.06);
    return 0;
  }
  if (p < 0.12) return (p - 0.06) / 0.06;
  if (p < 0.5) return 0.94 + (p - 0.12) * 0.15;
  if (p < 0.56) return Math.max(0, 1 - (p - 0.5) / 0.06);
  return 0;
};

function Trace({ kind, params }) {
  const ref = useRef(null);
  const live = useRef(params);
  live.current = params;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0;
    let H = 0;
    const size = () => {
      const r = canvas.getBoundingClientRect();
      W = Math.max(50, Math.floor(r.width * dpr));
      H = Math.max(20, Math.floor(r.height * dpr));
      canvas.width = W;
      canvas.height = H;
      ctx.fillStyle = "#0b1419";
      ctx.fillRect(0, 0, W, H);
    };
    size();
    // The canvas changes size with the window, the split view and a phone's rotation.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => size()) : null;
    if (ro) ro.observe(canvas);
    const onResize = () => size();
    window.addEventListener("resize", onResize);

    let x = 0;
    let T = 0;
    let lastY = null;
    // A shock only draws when it's new: opening the monitor mid-run doesn't replay an old one.
    let seenShock = live.current && live.current.shockId;
    let shockT = null;
    let last = performance.now();
    let raf = 0;

    const valueAt = (t) => {
      const p = live.current;
      if (!p || !p.on) return null;
      if (kind === "ecg") {
        if (shockT !== null && t - shockT >= 0 && t - shockT < SHOCK_MS) {
          const d = t - shockT;
          if (d < 25) return H * 0.04;
          if (d < 60) return H * 0.96;
          return H * 0.58 + H * 0.3 * Math.exp(-(d - 60) / 140);
        }
        const s = p.samples;
        if (!s || !s.length) return H * 0.55;
        const i = Math.floor((((t % p.duration) + p.duration) % p.duration) / 4) % s.length;
        const v = (s[i] ? s[i][1] : 0) * (p.cpr ? 0.35 : 1) + (p.cpr ? cprArtifact(t) : 0);
        return H * 0.58 - v * (H / 3.2);
      }
      if (kind === "pleth") {
        if (!p.rate) return H * 0.82;
        const period = 60000 / p.rate;
        return H * 0.85 - plethAt((t % period) / period) * H * 0.7 * p.amp;
      }
      if (!p.rate) return H * 0.88;
      const period = 60000 / p.rate;
      return H * 0.88 - co2At((t % period) / period, p.shape) * H * 0.75 * p.amp;
    };

    const frame = (now) => {
      const dt = Math.max(0, Math.min(100, now - last)); // rAF can report a time just before `last`
      last = now;
      const cur = live.current;
      if (cur && cur.shockId && cur.shockId !== seenShock) {
        seenShock = cur.shockId;
        shockT = T;
      }
      const pxPerMs = W / SWEEP_MS;
      const steps = Math.max(1, Math.ceil(dt * pxPerMs));
      ctx.lineWidth = 2.2 * dpr;
      ctx.lineJoin = "round";
      ctx.strokeStyle = COLORS[kind];
      for (let k = 0; k < steps; k++) {
        const nx = x + (dt * pxPerMs) / steps;
        T += dt / steps;
        const y = valueAt(T);
        // erase a little ahead of the sweep
        ctx.fillStyle = "#0b1419";
        ctx.fillRect(Math.floor(nx) + 1, 0, 14 * dpr, H);
        if (nx >= W) {
          x = 0;
          lastY = null;
          continue;
        }
        if (y !== null && lastY !== null) {
          ctx.beginPath();
          ctx.moveTo(x, lastY);
          ctx.lineTo(nx, y);
          ctx.stroke();
        }
        lastY = y;
        x = nx;
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [kind]);

  return <canvas ref={ref} className={`lm-trace lm-trace--${kind}`} aria-hidden="true" />;
}

// A number that eases toward its target instead of jumping.
function useEased(target, perSecond) {
  const [value, setValue] = useState(target);
  const targetRef = useRef(target);
  targetRef.current = target;
  useEffect(() => {
    const id = setInterval(() => {
      setValue((v) => {
        const t = targetRef.current;
        if (t === null || t === undefined) return t;
        if (v === null || v === undefined) return t;
        if (Math.abs(t - v) <= perSecond / 2) return t;
        return v + Math.sign(t - v) * (perSecond / 2);
      });
    }, 500);
    return () => clearInterval(id);
  }, [perSecond]);
  return value;
}

const round = (v) => (v === null || v === undefined ? null : Math.round(v));

function MonitorFace({ state, onAcquire, buttonsSlot }) {
  const set = state.set || {};
  const att = state.attached || {};
  const rhythm = normalizeRhythm(set.rhythm) || "Normal Sinus Rhythm";
  const hr = set.noPulse && /fibrillation|asystole/i.test(rhythm) ? null : set.hr;
  const shapeKey = state.patternKey && !isRhythmOnlyPattern(state.patternKey) ? state.patternKey : "";
  const ecgRate = Math.max(20, Math.min(220, set.hr || 60));

  const samples = useMemo(() => {
    try {
      return sampleLead({ rhythm, hr: ecgRate, lead: "II", patternKey: shapeKey, durationMs: 12000, step: 4, seed: 7 });
    } catch (e) {
      return [];
    }
  }, [rhythm, ecgRate, shapeKey]);

  const arrest = state.arrest || {};
  const cpr = !!arrest.cpr;
  const ecgOn = !!att.ecg || !!arrest.pads;
  // In arrest, CO2 comes from compressions and bagging: low (the scenario's number if it gives one) with about
  // 10 breaths a minute during CPR, a flat line when compressions stop. A set with a pulse brings it back up (ROSC).
  const co2Target = set.noPulse ? (cpr ? (set.etco2Estimated ? 14 : set.etco2) : 0) : set.etco2;
  const co2Rate = set.noPulse ? (cpr ? 10 : 0) : set.rr || 0;

  const eHr = round(useEased(hr, 6));
  const eSpo2 = round(useEased(set.noPulse ? null : set.spo2, 2));
  const eCo2 = round(useEased(co2Target, set.noPulse ? 10 : 3)); // capnography changes breath to breath in arrest
  const eRr = round(useEased(set.noPulse ? co2Rate : set.rr, 2));
  const perfusionAmp = set.noPulse ? (cpr ? 0.3 : 0) : set.sys && set.sys < ((state.patient && state.patient.sbpFloor) || 90) ? 0.45 : 0.85;
  const plethRate = set.noPulse ? (cpr ? 110 : 0) : set.hr;

  const apnea = !!att.etco2 && co2Rate <= 0;
  // Alarm limits by age. Adults keep 50 and 130. A child alarms outside its normal range (10 under, 20 over);
  // a newborn alarms under 100, where ventilation is due. A newborn in its first 10 minutes has the rising
  // SpO2 targets of the first minutes of life, not 90%.
  const patient = state.patient || {};
  const band = BANDS.find((b) => b.key === patient.band) || BANDS[BANDS.length - 1];
  const hrLimits = band.key === "adult" ? [50, 130] : [band.hrLowRed || band.hr[0] - 10, band.hr[1] + 20];
  const minuteOfLife = patient.ageMinutes !== null && patient.ageMinutes !== undefined ? patient.ageMinutes + (state.elapsedMs || 0) / 60000 : null;
  const spo2Floor = band.key === "newborn" ? newbornSpo2Floor(minuteOfLife) ?? 90 : 90;
  const alarm = {
    hr: ecgOn && ((eHr !== null && (eHr < hrLimits[0] || eHr > hrLimits[1])) || (!!set.noPulse && !cpr)),
    spo2: att.spo2 && eSpo2 !== null && eSpo2 < spo2Floor,
    co2: att.etco2 && (apnea || (eCo2 !== null && (eCo2 > 50 || eCo2 < 30))),
  };
  const reveal = state.reveal || {};
  const others = [
    reveal.bgl && set.bgl !== null && set.bgl !== undefined ? `BGL ${set.bgl} mmol/L` : "",
    reveal.temp && set.temp !== null && set.temp !== undefined ? `Temp ${set.temp} °C` : "",
  ].filter(Boolean);

  const cell = (key, label, value, unit, sub, on, isAlarm) => (
    <div className={`lm-num lm-num--${key}${isAlarm ? " lm-alarm" : ""}`}>
      <div className="lm-num-label">{label}</div>
      <div className="lm-num-value">
        {on && value !== null && value !== undefined ? value : "---"}
        <span className="lm-num-unit">{unit}</span>
      </div>
      <div className="lm-num-sub">{on ? sub : "not connected"}</div>
    </div>
  );

  return (
    <div className="lm-face">
      <div className="lm-traces">
        <div className="lm-trace-label" style={{ color: COLORS.ecg }}>
          {!att.ecg && arrest.pads ? "Pads" : "II"} {ecgOn ? "" : "· leads off"}
        </div>
        <Trace kind="ecg" params={{ on: ecgOn, samples, duration: 12000, cpr, shockId: arrest.shockId || 0 }} />
        <div className="lm-trace-label" style={{ color: COLORS.pleth }}>Pleth {att.spo2 ? "" : "· probe off"}</div>
        <Trace kind="pleth" params={{ on: !!att.spo2, rate: plethRate, amp: perfusionAmp }} />
        <div className="lm-trace-label" style={{ color: COLORS.co2 }}>
          CO2 {att.etco2 ? "" : "· not connected"}
          {apnea && <span className="lm-apnea">APNEA</span>}
        </div>
        <div className="lm-co2-wrap">
          <Trace kind="co2" params={{ on: !!att.etco2, rate: co2Rate, shape: set.noPulse ? "normal" : state.co2Shape, amp: Math.max(0.08, Math.min(1.1, (eCo2 || 0) / 60)) }} />
          {att.etco2 && (
            <div className="lm-co2-scale" aria-hidden="true">
              <span style={{ top: "25.5%" }}>50</span>
              <span style={{ top: "88%" }}>0</span>
            </div>
          )}
        </div>
        <EcgPrintouts prints={state.prints} ecgOn={!!att.ecg} onAcquire={onAcquire} ecg={state.ecg} patternKey={state.patternKey} buttonsSlot={buttonsSlot} />
      </div>
      <div className="lm-nums">
        {cell("hr", "HR", eHr, "bpm", cpr ? "CPR in progress" : ecgOn ? rhythm : "", ecgOn, alarm.hr)}
        {cell("spo2", "SpO2", eSpo2, "%", "", !!att.spo2, alarm.spo2)}
        {cell("co2", "EtCO2", eCo2, "mmHg", eRr !== null ? `awRR ${eRr}` : "", !!att.etco2, alarm.co2)}
        {cell("nibp", "NIBP", state.nibp ? `${state.nibp.sys}/${state.nibp.dia}` : null, "mmHg",
          state.nibp ? `taken at ${state.nibp.at}` : "not taken yet", true, false)}
        {others.length > 0 && (
          <div className="lm-num lm-num--other">
            {others.map((o) => <span key={o}>{o}</span>)}
          </div>
        )}
      </div>
    </div>
  );
}

// state: pass it to embed the monitor beside the controls; leave it out to listen for the instructor's window.
export default function LiveMonitor({ state: embeddedState, embedded = false, onAcquire }) {
  const [remote, setRemote] = useState(() => (embedded ? null : readLiveState()));
  const [slot, setSlot] = useState(null);
  const channelRef = useRef(null);

  useEffect(() => {
    if (embedded) return undefined;
    const ch = openLiveChannel((msg) => {
      if (msg && msg.type === "state") setRemote(msg.state);
    });
    channelRef.current = ch;
    ch.post({ type: "hello" });
    const beat = setInterval(() => ch.post({ type: "here" }), 3000);
    return () => {
      clearInterval(beat);
      channelRef.current = null;
      ch.close();
    };
  }, [embedded]);

  const state = embedded ? embeddedState : remote;
  const [full, setFull] = useState(false);
  const canFull = !embedded && typeof document !== "undefined" && document.fullscreenEnabled;
  const toggleFull = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen().then(() => setFull(true)).catch(() => {});
    else document.exitFullscreen().then(() => setFull(false)).catch(() => {});
  };

  return (
    <div className={`lm-root${embedded ? " lm-embedded" : ""}`}>
      <div className="lm-topbar">
        <span className="lm-top-title">Patient monitor</span>
        {state && !state.ended && <span className="lm-top-age">{(state.patient && state.patient.label) || "Adult"}</span>}
        <span className="lm-top-status">{state && !state.ended ? `${state.running ? "running" : "paused"} · ${state.clock || "00:00"}` : ""}</span>
        <span className="lm-top-slot" ref={setSlot} />
        {canFull && (
          <button type="button" className="lm-full-btn" onClick={toggleFull}>
            {full ? "Exit full screen" : "Full screen"}
          </button>
        )}
      </div>
      {!state || state.ended ? (
        <div className="lm-waiting">
          <p className="lm-waiting-title">{state && state.ended ? "The monitor screen was closed." : "Waiting for the instructor."}</p>
          <p>
            On the instructor's screen, open a scenario, press <strong>Monitor screen</strong>, then <strong>Pop out the monitor</strong>.
            This window updates on its own.
          </p>
        </div>
      ) : (
        <MonitorFace
          state={state}
          buttonsSlot={slot}
          onAcquire={embedded ? onAcquire : (leads) => channelRef.current && channelRef.current.post({ type: "acquire", leads })}
        />
      )}
    </div>
  );
}
